// MatrixVisualization - 2x2 matrix visualization for Value/Effort and Eisenhower
// Implements US-5.2: 2x2 Matrix Visualizations (Value/Effort, Eisenhower)

import React, { useMemo } from 'react';
import { Item, Framework } from '../../types/domain';
import styles from './MatrixVisualization.module.css';

interface MatrixVisualizationProps {
  items: Item[];
  framework: Framework;
}

/**
 * Quadrant configuration for matrix frameworks
 */
interface QuadrantConfig {
  id: string;
  label: string;
  description: string;
  color: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

/**
 * Item with calculated quadrant
 */
interface MatrixItem {
  id: string;
  title: string;
  quadrant: string;
  criteria: { [key: string]: number | string };
}

/**
 * CALCULATE quadrant for Value/Effort framework
 * Quadrants: Quick Wins, Major Projects, Fill-Ins, Time Wasters
 */
const calculateValueEffortQuadrant = (value: number, effort: number): string => {
  if (value >= 3 && effort <= 2) {
    return 'Quick Wins';
  } else if (value >= 3 && effort >= 3) {
    return 'Major Projects';
  } else if (value <= 2 && effort <= 2) {
    return 'Fill-Ins';
  } else {
    return 'Time Wasters';
  }
};

/**
 * CALCULATE quadrant for Eisenhower framework (Categorical)
 * NOTE: Eisenhower uses categorical values (not numeric)
 * Quadrants: Do First, Schedule, Delegate, Eliminate
 */
const calculateEisenhowerQuadrant = (urgency: string, importance: string): string => {
  if (urgency === 'urgent' && importance === 'important') {
    return 'Do First';
  } else if (urgency === 'not-urgent' && importance === 'important') {
    return 'Schedule';
  } else if (urgency === 'urgent' && importance === 'not-important') {
    return 'Delegate';
  } else {
    return 'Eliminate';
  }
};

/**
 * GET quadrant configurations for framework
 */
const getQuadrantConfigs = (frameworkSlug: string): QuadrantConfig[] => {
  if (frameworkSlug === 'value-effort') {
    return [
      {
        id: 'quick-wins',
        label: 'Quick Wins',
        description: 'Hoher Value, Geringer Effort',
        color: '#10b981', // Green
        position: 'top-left',
      },
      {
        id: 'major-projects',
        label: 'Major Projects',
        description: 'Hoher Value, Hoher Effort',
        color: '#eab308', // Yellow
        position: 'top-right',
      },
      {
        id: 'fill-ins',
        label: 'Fill-Ins',
        description: 'Geringer Value, Geringer Effort',
        color: '#3b82f6', // Blue
        position: 'bottom-left',
      },
      {
        id: 'time-wasters',
        label: 'Time Wasters',
        description: 'Geringer Value, Hoher Effort',
        color: '#ef4444', // Red
        position: 'bottom-right',
      },
    ];
  } else if (frameworkSlug === 'eisenhower') {
    return [
      {
        id: 'do-first',
        label: 'Do First',
        description: 'Dringlich & Wichtig',
        color: '#ef4444', // Red
        position: 'top-right',
      },
      {
        id: 'schedule',
        label: 'Schedule',
        description: 'Nicht dringlich, Wichtig',
        color: '#10b981', // Green
        position: 'top-left',
      },
      {
        id: 'delegate',
        label: 'Delegate',
        description: 'Dringlich, Nicht wichtig',
        color: '#eab308', // Yellow
        position: 'bottom-right',
      },
      {
        id: 'eliminate',
        label: 'Eliminate',
        description: 'Nicht dringlich, Nicht wichtig',
        color: '#6b7280', // Gray
        position: 'bottom-left',
      },
    ];
  }
  return [];
};

/**
 * GET axis labels for framework
 */
const getAxisLabels = (frameworkSlug: string) => {
  if (frameworkSlug === 'value-effort') {
    return {
      xAxis: 'Effort',
      xAxisLow: 'Geringer Effort',
      xAxisHigh: 'Hoher Effort',
      yAxis: 'Value',
      yAxisLow: 'Geringer Value',
      yAxisHigh: 'Hoher Value',
    };
  } else if (frameworkSlug === 'eisenhower') {
    return {
      xAxis: 'Dringlichkeit',
      xAxisLow: 'Nicht dringend',
      xAxisHigh: 'Dringend',
      yAxis: 'Wichtigkeit',
      yAxisLow: 'Nicht wichtig',
      yAxisHigh: 'Wichtig',
    };
  }
  return {
    xAxis: '',
    xAxisLow: '',
    xAxisHigh: '',
    yAxis: '',
    yAxisLow: '',
    yAxisHigh: '',
  };
};

/**
 * MATRIX VISUALIZATION component
 * - Displays 2x2 matrix for Value/Effort or Eisenhower frameworks
 * - Groups items into quadrants based on criteria scores
 * - Shows unscored items separately
 * - Shows empty state when no items scored
 */
export const MatrixVisualization: React.FC<MatrixVisualizationProps> = ({ items, framework }) => {
  /**
   * PROCESS items and group by quadrant
   */
  const { matrixItems, unscoredItems, quadrants } = useMemo(() => {
    const quadrantConfigs = getQuadrantConfigs(framework.slug);
    const scored: MatrixItem[] = [];
    const unscored: Item[] = [];

    // PROCESS each item
    items.forEach((item) => {
      if (framework.slug === 'value-effort') {
        const value = Number(item.scores?.find((s) => s.criterion === 'value')?.value || 0);
        const effort = Number(item.scores?.find((s) => s.criterion === 'effort')?.value || 0);

        // VALIDATE: Only process if both scores exist
        if (value > 0 && effort > 0) {
          scored.push({
            id: item.id,
            title: item.title,
            quadrant: calculateValueEffortQuadrant(value, effort),
            criteria: { value, effort },
          });
        } else {
          unscored.push(item);
        }
      } else if (framework.slug === 'eisenhower') {
        const urgency = String(item.scores?.find((s) => s.criterion === 'urgency')?.value || '');
        const importance = String(
          item.scores?.find((s) => s.criterion === 'importance')?.value || ''
        );

        // VALIDATE: Only process if both scores exist
        if (urgency && importance) {
          scored.push({
            id: item.id,
            title: item.title,
            quadrant: calculateEisenhowerQuadrant(urgency, importance),
            criteria: { urgency, importance },
          });
        } else {
          unscored.push(item);
        }
      }
    });

    // GROUP items by quadrant
    const quadrantMap = quadrantConfigs.map((config) => ({
      ...config,
      items: scored.filter((item) => item.quadrant === config.label),
    }));

    return {
      matrixItems: scored,
      unscoredItems: unscored,
      quadrants: quadrantMap,
    };
  }, [items, framework.slug]);

  const axisLabels = getAxisLabels(framework.slug);

  // RENDER empty state if no items are scored
  if (matrixItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <h3>Füge Themen / Elemente und Bewertungen hinzu, um Ergebnisse zu sehen</h3>
        <p>Bewerte mindestens ein Thema / Element, um die Matrix-Visualisierung zu sehen</p>
      </div>
    );
  }

  // ORGANIZE quadrants by position for grid layout
  const topLeft = quadrants.find((q) => q.position === 'top-left');
  const topRight = quadrants.find((q) => q.position === 'top-right');
  const bottomLeft = quadrants.find((q) => q.position === 'bottom-left');
  const bottomRight = quadrants.find((q) => q.position === 'bottom-right');

  return (
    <div className={styles.container}>
      {/* MATRIX SECTION */}
      <div className={styles.matrixWrapper}>
        {/* Y-AXIS LABEL (Vertical) */}
        <div className={styles.yAxisLabel}>
          <div className={styles.yAxisText}>{axisLabels.yAxis}</div>
          <div className={styles.yAxisHigh}>{axisLabels.yAxisHigh}</div>
          <div className={styles.yAxisLow}>{axisLabels.yAxisLow}</div>
        </div>

        {/* MATRIX GRID */}
        <div className={styles.matrixContainer}>
          <div className={styles.matrixGrid}>
            {/* TOP LEFT QUADRANT */}
            {topLeft && (
              <div
                className={styles.quadrant}
                style={{ borderColor: topLeft.color }}
                data-position="top-left"
              >
                <div className={styles.quadrantHeader} style={{ backgroundColor: topLeft.color }}>
                  <h4>{topLeft.label}</h4>
                  <p>{topLeft.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {topLeft.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    topLeft.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* TOP RIGHT QUADRANT */}
            {topRight && (
              <div
                className={styles.quadrant}
                style={{ borderColor: topRight.color }}
                data-position="top-right"
              >
                <div className={styles.quadrantHeader} style={{ backgroundColor: topRight.color }}>
                  <h4>{topRight.label}</h4>
                  <p>{topRight.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {topRight.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    topRight.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BOTTOM LEFT QUADRANT */}
            {bottomLeft && (
              <div
                className={styles.quadrant}
                style={{ borderColor: bottomLeft.color }}
                data-position="bottom-left"
              >
                <div
                  className={styles.quadrantHeader}
                  style={{ backgroundColor: bottomLeft.color }}
                >
                  <h4>{bottomLeft.label}</h4>
                  <p>{bottomLeft.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {bottomLeft.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    bottomLeft.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BOTTOM RIGHT QUADRANT */}
            {bottomRight && (
              <div
                className={styles.quadrant}
                style={{ borderColor: bottomRight.color }}
                data-position="bottom-right"
              >
                <div
                  className={styles.quadrantHeader}
                  style={{ backgroundColor: bottomRight.color }}
                >
                  <h4>{bottomRight.label}</h4>
                  <p>{bottomRight.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {bottomRight.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    bottomRight.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* X-AXIS LABELS (Horizontal) */}
          <div className={styles.xAxisLabels}>
            <span className={styles.xAxisLow}>{axisLabels.xAxisLow}</span>
            <span className={styles.xAxisCenter}>{axisLabels.xAxis}</span>
            <span className={styles.xAxisHigh}>{axisLabels.xAxisHigh}</span>
          </div>
        </div>
      </div>

      {/* UNSCORED ITEMS - Show at bottom if any */}
      {unscoredItems.length > 0 && (
        <div className={styles.unscoredSection}>
          <h4 className={styles.unscoredTitle}>Nicht bewertete Themen / Elemente ({unscoredItems.length})</h4>
          <div className={styles.unscoredList}>
            {unscoredItems.map((item) => (
              <div key={item.id} className={styles.unscoredItem}>
                <span className={styles.unscoredIcon}>○</span>
                <span className={styles.unscoredItemTitle}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
